import { AlertTriangle, RefreshCcw } from "lucide-react";
import { useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { useEffect } from "react";

import { Button } from "@/components/motion/button";
import { ButtonRouterLink } from "@/components/motion/button/router-link";
import { captureHandledError } from "@/lib/glitchtip";
import { getErrorMessage } from "@/lib/errors";

export function AppRouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();

  useEffect(() => {
    void captureHandledError({
      error,
      options: {
        tags: {
          area: "router",
          surface: "browser",
        },
        contexts: {
          route_error: {
            message: getErrorMessage(error, "Route error"),
          },
        },
      },
    });
  }, [error]);

  const handleRetry = async () => {
    await router.invalidate();
    reset();
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6 py-16 text-foreground">
      <div className="w-full max-w-xl border border-dashed border-hb-card bg-hb-surface p-8 text-center md:p-10">
        <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full border border-dashed border-hb-danger-border bg-hb-danger-surface text-hb-danger-text">
          <AlertTriangle className="h-5 w-5" />
        </div>
        <h1 className="font-hb text-2xl font-extrabold tracking-tight text-hb-heading">
          Something went wrong
        </h1>
        <p className="mx-auto mt-3 max-w-md font-mono-hb text-[11px] leading-relaxed text-hb-muted">
          {getErrorMessage(error, "We hit an unexpected problem while rendering this page.")}
        </p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button size="lg" type="button" onClick={() => void handleRetry()}>
            <RefreshCcw className="h-4 w-4" />
            Retry
          </Button>
          <ButtonRouterLink size="lg" to="/" variant="outline">
            Back home
          </ButtonRouterLink>
        </div>
      </div>
    </main>
  );
}
